"use client";

import { cn, variantBackgroundColors } from "@/lib/utils";
import React from "react";
import { ButtonStyleType, SpacingType } from "@/types";
import { sectionVariants } from "@/types";
import { Button } from "@/components/ui/button";
import Link from "next/link";
import parse from "html-react-parser";
import Image from '@/components/ui/Image';
import { SectionWrapper } from "../components/SectionWraper";
import { Badge } from "../ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";

interface Feature16Props { 
  darkMode?: boolean; 
  variant?: sectionVariants;
  spacing?: SpacingType;
  usedInPageNav?: boolean;
  pageNavTitle?: string;
  removePaddingBottom?: boolean;
  themeChangeOnScroll?: boolean;
  badge?: string;
  title?: string;
  content?: string;
  hasButton?: boolean;
  buttonText?: string;
  buttonLink?: string;
  buttonStyle?: ButtonStyleType;
  microCopy?: string;
  secondaryButton?: boolean;
  secondaryButtonText?: string;
  secondaryButtonLink?: string;
  secondaryButtonStyle?: ButtonStyleType;
  items?: {
    title?: string;
    content?: string;
    tabShortDescription?: string;
    link?: string;
    image?: string;
    imageAltText?: string;
    buttonText?: string;
    buttonLink?: string;
    buttonStyle?: ButtonStyleType;
  }[];
  attributes?: React.HTMLProps<HTMLElement>;
}

const Feature16 = ({
  darkMode = false,
  variant = "A",
  spacing,
  usedInPageNav = false,
  pageNavTitle = "Feature 16",
  removePaddingBottom = false,
  themeChangeOnScroll = false,
  badge,
  title,
  content,
  hasButton,
  buttonText,
  buttonLink,
  buttonStyle,
  microCopy,
  secondaryButton,
  secondaryButtonText,
  secondaryButtonLink,
  secondaryButtonStyle,
  items,
  attributes,
}: Feature16Props) => {

  const tabsBackground = variantBackgroundColors(darkMode, variant);

  return (
    <SectionWrapper
      usedInPageNav={usedInPageNav}
      pageNavTitle={pageNavTitle}
      removePaddingBottom={removePaddingBottom}
      themeChangeOnScroll={themeChangeOnScroll}
      spacing={spacing}
      variant={variant}
      darkMode={darkMode}
      heroSection={false}
      componentName="feature16"
      attributes={attributes}
    >

      <div className="container">
        <div className="flex flex-col justify-between gap-6 md:gap-16 lg:flex-row pb-6 md:pb-20" data-aos="fade-up">
          {title && (
            <div className="lg:w-1/2 text-center lg:text-left">
              {badge && (
                <Badge variant="outline" bold className="mb-6">
                  {badge}
                </Badge>
              )}
              <h2>
                {parse(title)}
              </h2>
            </div>
          )}
          {(content || hasButton) && (
            <div className="lg:w-1/2 flex flex-col gap-6">
              {content && (
                <div className="text-muted-foreground text-center lg:text-left">
                  {parse(content)}
                </div>
              )}
              {hasButton && (
                <div className="flex flex-col md:flex-row gap-4 justify-center lg:justify-start">
                  <div className="flex flex-col">
                    <Button asChild variant={buttonStyle} size="lg" className="w-full sm:w-auto">
                      <Link href={buttonLink ?? ""}>
                        {buttonText}
                      </Link>
                    </Button>
                    {microCopy && (
                      <p className="paragraph-sm text-muted-foreground text-center lg:text-left mt-3">
                        {microCopy}
                      </p>
                    )}
                  </div>
                  {secondaryButton && (
                    <Button asChild variant={secondaryButtonStyle} size="lg" className="w-full sm:w-auto">
                      <Link href={secondaryButtonLink ?? ""}>
                        {secondaryButtonText}
                      </Link>
                    </Button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
        
        <Tabs
          defaultValue="0"
          orientation="vertical"
          className={cn(
            "flex flex-col md:flex-row gap-10 md:gap-4 rounded-2xl p-4",
            tabsBackground
          )}
          data-aos="fade-up"
          data-aos-delay={100}
        >
          <TabsList className="flex flex-col justify-start items-stretch p-4 bg-transparent gap-2 h-full w-full md:w-[50%] md:max-w-[50%] border-1 md:border-none">
            {items?.map((item, index) => (
              <TabsTrigger
                key={index}
                value={index.toString()}
                className={cn(
                  "w-full justify-start rounded-2xl text-start whitespace-normal shadow-none block h-auto p-0 lg:max-w-[80%] xl:max-w-[70%]",
                  "transition-all duration-300 cursor-pointer",
                  "data-[state=active]:[&>div>h5]:font-bold md:[&>div>h5]:font-bold",
                  "data-[state=active]:border-brand",
                  variant === "B" && `data-[state=active]:[&>div]:bg-muted`,
                  variant === "A" && `data-[state=active]:[&>div]:bg-background`,
                )}
              >
                <div className={cn(
                  "px-3 py-4 md:px-6 rounded-xl transition-all duration-300",
                  // variant === "B" && `bg-background`,
                  // variant === "A" && `bg-muted`,
                )}>
                  <h6>{item.title}</h6>
                  <p className="hidden md:block mt-2 paragraph-sm text-muted-foreground">
                    {item.tabShortDescription}
                  </p>
                </div>
              </TabsTrigger>
            ))}
          </TabsList>

          {items?.map((item, index) => (
            <TabsContent
              className={cn(
                "overflow-hidden rounded-xl bg-transparent lg:col-span-2 md:max-w-[50%] md:ml-auto",

              )}
              key={index}
              value={index.toString()}
            >
              <div className="pb-6 md:pt-10 md:pb-14">
                {item.title && (
                  <h4 className="mb-4 md:mb-8">
                    {item.title}
                  </h4>
                )}
                {item.content && (
                  <div className="mt-2 lg:pr-20">
                    {parse(item.content)}
                  </div>
                )}
                {item.buttonText && (
                  <Button asChild variant={item.buttonStyle} size="lg" className="w-full sm:w-auto mt-6 md:mt-10">
                    <Link href={item.buttonLink ?? ""}>
                      {item.buttonText}
                    </Link>
                  </Button>
                )}
              </div>
              {item.image && (
                <div className="aspect-[643/480]">      
                  <Image
                    src={item.image}
                    alt={item.imageAltText ?? ""}
                    title={item.imageAltText ?? ""}
                    className="rounded-2xl object-cover"
                    width={643}
                    height={480}
                  />
                </div>
              )}
            </TabsContent>
          ))}
        </Tabs>
      </div>
    </SectionWrapper>
  )
};

export { Feature16 };